/** F-73: e-mail campaigns. Mirrors the `@openapi` schemas of `campaign.routes.js`. */
import type { QuotaMeter } from '@/lib/sdk-official/resources/entitlements'

export type CampaignStatus = 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'PAUSED' | 'CANCELED'
export type CampaignRunStatus = 'QUEUED' | 'SENDING' | 'DONE' | 'SKIPPED' | 'FAILED'
export type OptOutScope = 'CAMPAIGNS' | 'ALL'

export type StaticBlockType = 'text' | 'button' | 'image'
/** Read once per send, only content every member can see. */
export type DynamicBlockType = 'latestPosts' | 'upcomingEvents' | 'newCourses' | 'newClassifieds' | 'course' | 'event' | 'product'
/** F-140: a locked block of a default e-mail (the password button, the ticket...), rendered by the platform's sender. */
export type SystemBlockType = 'systemSlot'
export type CampaignBlockType = StaticBlockType | DynamicBlockType | SystemBlockType
export type ScheduleKind = 'NOW' | 'ONCE' | 'DAILY' | 'WEEKLY'

export interface CampaignBlock {
  /** F-174: the block's persisted id (its edit lock and its own save, `PUT …/blocks/{id}`); a new block gets one on the next save. */
  id?: string
  type: CampaignBlockType
  html?: string
  text?: string
  url?: string
  alt?: string
  count?: number
  spaceId?: string | null
  withinDays?: number
  heading?: string
  courseId?: string
  eventId?: string
  productId?: string
  /** 1 to 6 items in the person's order (the singular field is the older, one-item form). */
  courseIds?: string[]
  eventIds?: string[]
  productIds?: string[]
  /** `systemSlot`: which locked block (`setPasswordButton`, `eventTicket`...). */
  slot?: string
}

/** People left out of the audience: anyone matching any of these, even if included. */
/** F-156: how a condition reads its field (SELECT `oneOf`; MULTI_SELECT `containsAny`/`containsAll`; BOOLEAN `isTrue`/`isFalse`). */
export type AudienceFieldOperator = 'oneOf' | 'containsAny' | 'containsAll' | 'isTrue' | 'isFalse'

/** F-156: one more part of the union (or of the exclusion), like a group or a tag. The API answers with `fieldId` and `key`. */
export interface AudienceFieldCondition {
  /** One of `fieldId` or `key` is sent; both come back. */
  fieldId?: string
  key?: string
  operator: AudienceFieldOperator
  /** Option `value`s; empty for BOOLEAN. */
  values: string[]
}

/** F-156: at most 10 field conditions, inclusion and exclusion together. */
export const MAX_AUDIENCE_FIELD_CONDITIONS = 10

/** F-156: a saved field condition that no longer picks whom it picked (the field went away or closed, an option was removed). */
export interface AudienceFieldWarning {
  scope: 'include' | 'exclude'
  index: number
  fieldId: string | null
  key: string | null
  reason: 'fieldNotFound' | 'fieldClosed' | 'fieldTypeChanged' | 'optionRemoved' | string
  removedValues?: string[]
}

export interface CampaignAudienceExclude {
  groupIds?: string[]
  tags?: string[]
  memberIds?: string[]
  fields?: AudienceFieldCondition[]
}

/** F-175: an item (only `content` for now) whose readers are the audience. */
export interface ItemAccessRef {
  type: string
  id: string
}

export interface CampaignAudience {
  all?: boolean
  groupIds?: string[]
  tags?: string[]
  memberIds?: string[]
  /** F-175: "who can open this item", a mode of its own (never with `all`, groups, tags or people); `exclude` still applies. */
  itemAccess?: ItemAccessRef | null
  /** F-156: never with `itemAccess` (the field exclusions still apply there). */
  fields?: AudienceFieldCondition[]
  exclude?: CampaignAudienceExclude
}

export interface CampaignRunCounters {
  delivered: number
  opened: number
  clicked: number
  bounced: number
  complained: number
  unsubscribed?: number
}

export interface CampaignRun {
  id: string
  status: CampaignRunStatus
  scheduledFor: string
  recipientCount: number
  skippedReason: string | null
  startedAt: string | null
  finishedAt: string | null
  counters: CampaignRunCounters
}

export interface Campaign {
  id: string
  name: string
  subject: string
  preheader: string | null
  blocks: CampaignBlock[]
  audience: CampaignAudience
  kind: 'MANUAL' | 'AUTOMATIC'
  status: CampaignStatus
  scheduleKind: ScheduleKind
  /** One-off send, UTC. */
  sendAt?: string | null
  /** Next firing, UTC. */
  nextRunAt?: string | null
  /** Recurring: 0 = Sunday. */
  weekdays?: number[]
  /** Recurring: local time, `HH:mm`. */
  timeOfDay?: string | null
  systemKey?: string | null
  enabled?: boolean
  intro?: string | null
  consentConfirmedAt: string | null
  createdAt: string
  updatedAt: string
  /** F-174 (CAMP-6): the version of the blocks read; a whole save with `blocks` sends it back (stale: 409 with the current blocks). */
  blocksUpdatedAt?: string | null
  lastRun?: CampaignRun | null
  runs?: CampaignRun[]
  /** F-156: detail only; field conditions of the saved audience that no longer pick whom they picked (often `[]`). */
  audienceWarnings?: AudienceFieldWarning[]
  /** F-140: the library e-mail this send uses. */
  emailId?: string | null
  email?: { id: string; name: string; mainLanguage: string; languages: string[]; systemKey: string | null } | null
  /** F-175: the item this draft was made from ("Enviar por e-mail"), or null. */
  sourceItem?: ItemAccessRef | null
}

export interface CampaignDraft {
  name: string
  /** F-140: with `emailId`, the content comes from that library e-mail and `subject` and `blocks` are left out. */
  emailId?: string
  subject?: string
  preheader?: string | null
  blocks?: CampaignBlock[]
  audience: CampaignAudience
}

/** F-174: one block saved on its own (`PUT …/blocks/{blockId}`). */
export interface BlockSaveResult {
  blockId: string
  language: string | null
  block: CampaignBlock | null
  updatedAt: string | null
}

export interface CampaignQuota {
  timeZone: string
  limits: { perDay: number; perWeek: number; perMonth: number }
  used: { day: number; week: number; month: number }
  remaining: { day: number; week: number; month: number }
  resetsAt: { day: string; week: string; month: string }
  overridden: boolean
  /** F-34: e-mail recipients left on the plan this period; a send bigger than `available` is refused (429 `quota_recipients`). */
  /** F-188: `viaOwnProvider` when the community's Resend account is active: the meter is the anti-abuse ceiling, not the plan's quota. */
  recipients?: QuotaMeter & { viaOwnProvider?: boolean }
}

export interface AudiencePreview {
  total: number
  candidates: number
  /** Distinct people in the selection before the `exclude` rule (`candidates` + `excluded.byRule`). */
  candidatesBeforeRule?: number
  excluded: { optedOut: number; hardBounce: number; complaint: number; byRule?: number }
  /** F-175: only with an `itemAccess` audience; a `status` other than `ok` means nobody receives it. */
  itemAccess?: ItemAccessRef & { title: string | null; status: 'ok' | 'notFound' | 'unavailable' | string }
  maxRecipients: number
  tooLarge: boolean
  sample: { memberId: string; name: string; email: string }[]
  /** F-156: only when some field condition no longer picks whom it picked. */
  fieldWarnings?: AudienceFieldWarning[]
}

export interface OptOut {
  id: string
  email: string
  scope: OptOutScope
  source: string
  createdAt: string
}

export interface SendResult {
  campaignId: string
  run: CampaignRun
  remaining: { day: number; week: number; month: number }
}

export interface ScheduleInput {
  scheduleKind: Exclude<ScheduleKind, 'NOW'>
  /** Local text in the community time zone, `YYYY-MM-DDTHH:mm`. Never converted. */
  sendAt?: string
  timeOfDay?: string
  weekdays?: number[]
  consent?: boolean
}

export interface CampaignReport {
  campaignId: string
  runs: number
  runsSkipped: number
  recipients: number
  delivered: number
  opened: number
  clicked: number
  bounced: number
  complained: number
  unsubscribed: number
  openedIsEstimate: boolean
}

export interface CampaignPreview {
  subject: string
  html: string
  empty: boolean
}

export type AutomaticBlockChoice = 'posts' | 'courses' | 'events' | 'classifieds'

export interface AutomaticCampaign extends Campaign {
  timeZone: string
  blockChoices: AutomaticBlockChoice[]
  availableBlockChoices: AutomaticBlockChoice[]
}

export interface AutomaticUpdate {
  enabled?: boolean
  consent?: boolean
  subject?: string
  intro?: string | null
  weekdays?: number[]
  timeOfDay?: string
  blocks?: AutomaticBlockChoice[]
}

/**
 * Totals and per-day series of every campaign send in a period
 * (`GET /campaigns/stats`, proposed: not in the API yet).
 */
export type CampaignStatsMetric = 'recipients' | 'delivered' | 'opened' | 'clicked' | 'unsubscribed'
export type CampaignStatsTotals = Record<CampaignStatsMetric, number>
/** `days` is `YYYY-MM-DD` in the community time zone, oldest first, one entry per day of the period. */
export interface CampaignStatsSeries { days: string[]; delivered: number[]; opened: number[]; clicked: number[]; unsubscribed: number[] }

export interface CampaignStats {
  /** When the numbers were computed (ISO instant). */
  generatedAt?: string
  /** The community time zone the days are cut in. */
  timeZone?: string
  days: number
  totals: CampaignStatsTotals
  series: CampaignStatsSeries
  /** The same number of days right before the period, aligned day by day with `series`. */
  previous?: { totals: CampaignStatsTotals; series: CampaignStatsSeries }
  /** `deltaPct` is null when the previous period is 0. */
  compare?: Record<CampaignStatsMetric, { current: number; previous: number; deltaPct: number | null }>
}

/** An item a fixed course, event or product block accepts (`GET /campaigns/block-items`). */
export interface CampaignBlockItem {
  id: string
  title: string
  image: string | null
  /** Events only. */
  startTime?: string
  /** Products only. */
  price?: number
  currency?: string
}
